import { BodySecondary } from '@liveauctioneers/hammer-ui-core/text';
import { ReactElement } from 'react';
import { useParams } from 'react-router-dom';

import { CategoryAd } from '@/components/Ads/CategoryAd';
import PageTitle from '@/components/PageTitle/PageTitle';
import { LeaderBoardAdTypes } from '@/redux/modules/catalog/ad/catalogAd.types';

import { AuctionsNearMeMapViewNew } from './components/MapView/AuctionsNearMeMapViewNew';
import { AuctionsNearMeCardsViewNew } from './components/MapView/components/CardsView/AuctionsNearMeCardsViewNew';
import { getPageDescriptionFromGeoArea } from './helpers/getPageDescription';
import { getPageTitle } from './helpers/getPageTitle';
import { useAuctionsNearMeData } from './hooks/useAuctionsNearMeData';
import AuctionsNearMePageMeta from './meta/AuctionsNearMePageMeta';
import { AuctionsNearMeRouteParams } from './types/AuctionsNearMe.types';

const AuctionsNearMePageNew = (): ReactElement => {
    const routeParams = useParams<AuctionsNearMeRouteParams>();

    const adId = 'auctions-near-me';
    const adPageName = `leaderboard-${adId}`;

    const {
        catalogsNearMe,
        geoArea,
        geocoderFailed,
        handleBoundsChange,
        handleGoogleMapsReady,
        handleKeywordsChange,
        handlePageChange,
        handlePageSizeChange,
        handleSearchResults,
        handleUserMapInteraction,
        hasFilteredResults,
        isGeocoding,
        loading,
        mapBounds,
        mapPoints,
        pageNumber,
        pageSize,
        searchTerm,
        setGeocoderFailed,
        setSearchTerm,
        toggleLocalPickupFilter,
        toggleTopRatedFilter,
        totalRecords,
        totalUniqueHouses,
    } = useAuctionsNearMeData();

    return (
        <>
            <CategoryAd
                adType={LeaderBoardAdTypes[adId]}
                pageName={adPageName}
            />
            <AuctionsNearMePageMeta
                catalogsNearMe={catalogsNearMe}
                geoArea={geoArea}
                routeParams={routeParams}
                searchTerm={searchTerm}
            />
            <section className="relative mx-auto flex min-h-[400px] w-full max-w-[1240px] flex-col px-md pb-xl pt-0">
                {/* Page Title and Description */}
                <section className="mb-lg">
                    <div className="mb-xs flex w-full justify-between">
                        <PageTitle
                            title={getPageTitle({
                                geoArea,
                                geocoderFailed,
                                hasFilteredResults,
                                routeParams,
                                searchTerm,
                            })}
                        />
                    </div>
                    <div className="max-w-[590px]">
                        <BodySecondary color="secondary">{getPageDescriptionFromGeoArea(geoArea)}</BodySecondary>
                    </div>
                </section>
                {/* Split container  */}
                <section className="mb-sm flex flex-col-reverse smMin:flex-row">
                    {/* CardsView */}
                    <div className="min-w-0 flex-1 smMin:mr-md">
                        <AuctionsNearMeCardsViewNew
                            catalogsNearMe={catalogsNearMe}
                            geoArea={geoArea}
                            handleKeywordsChange={handleKeywordsChange}
                            handlePageChange={handlePageChange}
                            handlePageSizeChange={handlePageSizeChange}
                            loading={loading}
                            pageNumber={pageNumber}
                            pageSize={pageSize}
                            toggleLocalPickupFilter={toggleLocalPickupFilter}
                            toggleTopRatedFilter={toggleTopRatedFilter}
                            totalRecords={totalRecords}
                        />
                    </div>
                    {/* MapView */}
                    <div className="mb-xs smMin:sticky smMin:top-lg smMin:mb-0 smMin:mt-lg smMin:h-fit smMin:w-[500px] smMin:flex-shrink-0">
                        <AuctionsNearMeMapViewNew
                            geoArea={geoArea}
                            isGeocoding={isGeocoding}
                            mapBounds={mapBounds}
                            mapPoints={mapPoints}
                            onBoundsChange={handleBoundsChange}
                            onGoogleMapsReady={handleGoogleMapsReady}
                            onSearchResults={handleSearchResults}
                            onUserMapInteraction={handleUserMapInteraction}
                            parentSearchTerm={searchTerm}
                            setGeocoderFailed={setGeocoderFailed}
                            setSearchTerm={setSearchTerm}
                            totalUniqueHouses={totalUniqueHouses}
                        />
                    </div>
                </section>
            </section>
        </>
    );
};

export default AuctionsNearMePageNew;
